import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { isMobile } from "react-device-detect";

import {
  getDeepLinkPublic,
  transformAccountResponse,
  transformChannelResponse,
  transformPrivateChannelResponse,
} from "../../api/vchat2";
import { APP_STORE_URL, VAMA_HOME_URL, buildOpenInVamaUrl } from "../../utils/links";
import { getDownloadTarget } from "../../utils/platform";
import Avatar from "./Avatar";
import GetAppModal from "./GetAppModal";
import PageHeader from "./PageHeader";

import "./style.css";

const memberCountFormat = new Intl.NumberFormat("en-US");

function isPrivateURL(pathname) {
  return pathname.includes("/i/");
}

/** Sendbird-era placeholder covers are not real group photos. */
function hasRealAvatar(url) {
  return Boolean(url) && !url.startsWith("https://static.sendbird.com/sample/cover");
}

/**
 * "Don't have Vama yet?" line under the main button. Phones go straight to
 * their store; desktops get the QR dialog.
 */
function GetAppHint({ onShowQr }) {
  return (
    <p className="vp-hint">
      Don't have Vama yet?{" "}
      {isMobile ? (
        <a href={getDownloadTarget().href}>Get the app</a>
      ) : (
        <button type="button" className="vp-link-button" onClick={onShowQr}>
          Get the app
        </button>
      )}
    </p>
  );
}

function ProfileCard({ account, username, openUrl, onShowQr }) {
  const fullName = `${account.first_name} ${account.last_name}`.trim();
  const handle = account.username || username;

  return (
    <section className="vp-card" aria-label={`${fullName} on Vama`}>
      <Avatar
        src={account.avatar}
        name={fullName}
        colorSeed={account.first_name + account.last_name}
      />
      <h1 className="vp-name">{fullName}</h1>
      {handle && <p className="vp-meta">@{handle}</p>}
      {account.textContent && <p className="vp-bio">{account.textContent}</p>}

      <div className="vp-actions">
        <a className="vp-btn" href={openUrl}>
          Send Message
        </a>
        <GetAppHint onShowQr={onShowQr} />
      </div>
    </section>
  );
}

function GroupCard({ group, username, openUrl, onShowQr }) {
  const name = group.metadata?.name || "";
  const description = group.metadata?.public?.description;
  const memberCount = group.member_count || 0;
  const nameWords = name.split(" ");

  return (
    <section className="vp-card" aria-label={`${name} on Vama`}>
      <Avatar
        src={hasRealAvatar(group.avatar) ? group.avatar : ""}
        name={name}
        colorSeed={(nameWords[0] || "") + (nameWords[1] || "")}
        initials={name.charAt(0).toUpperCase()}
      />
      <h1 className="vp-name">{name}</h1>
      <p className="vp-meta">
        {username && <span>@{username}</span>}
        {username && memberCount > 0 && <span className="vp-meta-dot">·</span>}
        {memberCount > 0 && (
          <span>
            {memberCountFormat.format(memberCount)}{" "}
            {memberCount === 1 ? "member" : "members"}
          </span>
        )}
      </p>
      {description && <p className="vp-bio">{description}</p>}

      <div className="vp-actions">
        <a className="vp-btn" href={openUrl}>
          Join Group
        </a>
        <GetAppHint onShowQr={onShowQr} />
      </div>
    </section>
  );
}

/** Legacy /i/<code> invite links — the web app has no route for them. */
function PrivateGroupCard({ channel }) {
  const name = channel.metadata?.name || "Private group";

  return (
    <section className="vp-card" aria-label={`${name} on Vama`}>
      <div className="vp-avatar vp-avatar-plain">
        <img src="/private_avatar.png" alt="" />
      </div>
      <h1 className="vp-name">{name}</h1>
      <p className="vp-meta">Private group</p>

      <div className="vp-actions">
        <a className="vp-btn" href={APP_STORE_URL}>
          Join Group
        </a>
        <p className="vp-hint">
          If you have Vama, open this link on your phone to join{" "}
          <strong>{name}</strong>.
        </p>
      </div>
    </section>
  );
}

function SkeletonCard() {
  return (
    <section className="vp-card vp-card-skeleton" aria-busy="true" aria-label="Loading">
      <div className="vp-skel vp-skel-avatar" />
      <div className="vp-skel vp-skel-line" style={{ width: "58%" }} />
      <div className="vp-skel vp-skel-line vp-skel-line-sm" style={{ width: "34%" }} />
      <div className="vp-skel vp-skel-button" />
    </section>
  );
}

function TempRedirect() {
  const { link } = useParams();
  const { pathname } = useLocation();
  const isPrivate = isPrivateURL(pathname);

  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQr, setShowQr] = useState(false);

  const openQr = useCallback(() => setShowQr(true), []);
  const closeQr = useCallback(() => setShowQr(false), []);

  // Where "Send Message" / "Join Group" take people for public user and group
  // links (same suffix the apps resolve — see utils/links.js).
  const openInVamaUrl = buildOpenInVamaUrl(link);

  useEffect(() => {
    if (!link) return undefined;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        // Legacy private invite links are stored with an "i/" prefix.
        const suffix = isPrivate ? `i/${link}` : link;
        const response = await getDeepLinkPublic(suffix);
        if (cancelled) return;

        let transformed = {};
        if (response.account) {
          transformed = transformAccountResponse(response.account);
        } else if (response.channel) {
          transformed = isPrivate
            ? transformPrivateChannelResponse(response.channel)
            : transformChannelResponse(response.channel);
        }
        setLinkData(transformed);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch deep link data:", err);
        // Unknown link: hand over to the marketing site.
        window.location.href = VAMA_HOME_URL;
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [link, isPrivate]);

  let card = null;
  if (loading || !linkData) {
    card = <SkeletonCard />;
  } else if (linkData.account) {
    card = (
      <ProfileCard
        account={linkData.account}
        username={link}
        openUrl={openInVamaUrl}
        onShowQr={openQr}
      />
    );
  } else if (linkData.public_link_channel) {
    card = (
      <GroupCard
        group={linkData.public_link_channel}
        username={link}
        openUrl={openInVamaUrl}
        onShowQr={openQr}
      />
    );
  } else if (linkData.private_link_channel) {
    card = <PrivateGroupCard channel={linkData.private_link_channel} />;
  }

  return (
    <div className="vp-page">
      <PageHeader />
      <main className="vp-main">{card}</main>
      <GetAppModal open={showQr} onClose={closeQr} />
    </div>
  );
}

export default TempRedirect;
